import { axiosInstanceTasks } from "../axios";
import { ClassName, FileHistories, RequestResponse } from "../types";

export const getAllFiles = async (
  pageNumber: number,
  pageSize: number,
  taskName?: string,
  syncClassName?: string,
  syncStatus?: string,
  moduleName?: string,
  dateFrom?: string,
  dateTo?: string,
  dateFilterColumn?: string
): Promise<RequestResponse<{ records: FileHistories[]; total: number }>> => {
  try {
    let url = `/file-histories?`;

    const params = new URLSearchParams();
    params.append("page", pageNumber.toString());
    params.append("size", pageSize.toString());

    if (taskName) {
      params.append("taskName", taskName);
    }

    if (syncClassName) {
      params.append("syncClassName", syncClassName);
    }

    if (syncStatus) {
      params.append("syncStatus", syncStatus);
    }

    if (moduleName) {
      params.append("moduleName", moduleName);
    }

    if (dateFrom) {
      const updatedDateFrom = `${dateFrom}T00:00:00`;
      params.append("dateFrom", updatedDateFrom);
    }
    if (dateTo) {
      const updatedDateTo = `${dateTo}T23:59:59`;
      params.append("dateTo", updatedDateTo);
    }
    if (dateFilterColumn) {
      params.append("dateFilterColumn", dateFilterColumn);
    }

    url += params.toString();

    const response = await axiosInstanceTasks.get(url);
    return {
      error: false,
      data: {
        records: response.data.data.records,
        total: response.data.data.total,
      },
    };
  } catch (err) {
    console.error("Unable to fetch history: ", err);
    return {
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: null,
    };
  }
};

export const getClassNames = async (): Promise<
  RequestResponse<ClassName[]>
> => {
  try {
    const response = await axiosInstanceTasks.get("/file-histories/classes");
    const classNamesData = response.data.data.map((item: any) => ({
      className: item.className,
      fullClass: item.fullClass,
    }));
    return {
      error: false,
      data: classNamesData,
    };
  } catch (err) {
    console.error("Unable to fetch class names: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: [],
    };
  }
};

export const fetchClassNames = async (): Promise<
  RequestResponse<ClassName[]>
> => {
  try {
    const response = await axiosInstanceTasks.get("/package-class");
    const classNamesData = response.data.data.map((item: any) => ({
      className: item.className,
      fullClass: item.fullClass,
    }));
    return {
      error: false,
      data: classNamesData,
    };
  } catch (err) {
    console.error("Unable to fetch class names: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: [],
    };
  }
};

export const dataResync = async (id: number): Promise<RequestResponse> => {
  try {
    const response = await axiosInstanceTasks.post(
      `file-histories/connection-data-resync?id=${id}`
    );
    return {
      error: false,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Unable to resync data: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: err.response?.data.data || null,
    };
  }
};

export const dataForceRetry = async (
  id: number,
  forceRetry: boolean
): Promise<RequestResponse> => {
  try {
    const response = await axiosInstanceTasks.post(
      `file-histories/sync-retry?id=${id}&forceRetry=${forceRetry}`
    );
    return {
      error: false,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Unable to force retry: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: err.response?.data.data || null,
    };
  }
};

export const dataRetry = async (id: number): Promise<RequestResponse> => {
  try {
    const response = await axiosInstanceTasks.post(
      `file-histories/sync-retry?id=${id}`
    );
    return {
      error: false,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Unable to retry data: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: err.response?.data.data || null,
    };
  }
};

export const getFileById = async (
  id: number
): Promise<RequestResponse<FileHistories>> => {
  try {
    const response = await axiosInstanceTasks.get(`/file-histories/${id}`);
    return {
      error: false,
      data: response.data.data,
    };
  } catch (err) {
    console.error("Unable to fetch file by ID:", err);
    return {
      error: true,
      message:
        err.response?.data?.error_description ||
        err.response?.data?.message ||
        err.message ||
        err,
      data: null,
    };
  }
};
